import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Inputz from "../ui/atoms/Inputz";
import Buttonz from "../ui/atoms/Buttonz";
import pb from "../services/pocketbase";
import { confirmPasswordReset, login } from "../auth/authApi";
import { pocketBaseErrorMessage } from "../auth/errors";
import { validateResetPassword } from "../auth/validation";

/** Link valid at most this long from token `iat` (aligned with email copy). */
const LINK_MAX_MS = 15 * 60 * 1000;
/** After opening this page, user must finish within this window. */
const PAGE_SESSION_MS = 15 * 60 * 1000;

const SESSION_KEY_PREFIX = "uud:pwdResetOpened:";

function getJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getLinkDeadlineMs(payload) {
  if (!payload?.exp) return 0;
  const expMs = Number(payload.exp) * 1000;
  const iatMs = payload.iat != null ? Number(payload.iat) * 1000 : null;
  if (iatMs != null && Number.isFinite(iatMs)) {
    return Math.min(expMs, iatMs + LINK_MAX_MS);
  }
  return expMs;
}

function getOpenedAtMs(token) {
  const key = SESSION_KEY_PREFIX + String(token).slice(0, 48);
  const raw = sessionStorage.getItem(key);
  if (raw != null && raw !== "") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  const now = Date.now();
  sessionStorage.setItem(key, String(now));
  return now;
}

function formatCountdown(ms) {
  if (ms <= 0) return "0:00";
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Password reset from email link (PocketBase token).
 * Routes: /auth/confirm-password-reset/:token and /recreate-password/:token
 */
export default function ConfirmPasswordReset() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [tick, setTick] = useState(0);

  const token = useMemo(() => {
    const q = searchParams.get("token");
    const p = params?.token;
    return String(p || q || "").trim();
  }, [params, searchParams]);

  const payload = useMemo(() => (token ? getJwtPayload(token) : null), [token]);

  const openedAtMs = useMemo(() => {
    if (!token) return 0;
    return getOpenedAtMs(token);
  }, [token]);

  const deadlineMs = useMemo(() => {
    if (!token || !payload) return 0;
    const linkEnd = getLinkDeadlineMs(payload);
    const pageEnd = openedAtMs + PAGE_SESSION_MS;
    return Math.min(linkEnd, pageEnd);
  }, [token, payload, openedAtMs]);

  const remainingMs = deadlineMs - Date.now();
  const expired = Boolean(token && payload && remainingMs <= 0);

  useEffect(() => {
    if (!token || !payload) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [token, payload]);

  const handleChange = (target) => {
    const { name, value } = target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const resolveProfilePath = () => {
    const u = pb.authStore.model;
    if (u?.username && String(u.username).trim()) {
      return `/${encodeURIComponent(String(u.username).trim())}`;
    }
    const payload = getJwtPayload(token);
    if (payload?.username && String(payload.username).trim()) {
      return `/${encodeURIComponent(String(payload.username).trim())}`;
    }
    return "/";
  };

  const handleSubmit = async () => {
    if (loading) return;
    if (!token) {
      setMessage("Invalid or missing reset link.");
      return;
    }
    if (!payload) {
      setMessage("Invalid reset link.");
      return;
    }
    if (Date.now() >= deadlineMs) {
      setMessage(
        "This reset link has expired. Please request a new password reset from the login screen."
      );
      return;
    }

    const { errors: vErr } = validateResetPassword({
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    });

    if (Object.keys(vErr).length > 0) {
      setErrors({
        password: Boolean(vErr.password),
        confirmPassword: Boolean(vErr.confirmPassword),
      });
      setMessage(vErr.password || vErr.confirmPassword || "Check your password fields.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      await confirmPasswordReset(token, formData.password);
      let path = resolveProfilePath();
      if (path === "/") {
        const payload = getJwtPayload(token);
        const em = payload?.email && String(payload.email).trim();
        if (em) {
          try {
            await login(em, formData.password);
            path = resolveProfilePath();
          } catch {
            /* still redirect below */
          }
        }
      }
      navigate(path, { replace: true });
    } catch (err) {
      setMessage(pocketBaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const showForm = Boolean(token && payload && !expired);

  return (
    <div style={{ padding: "2rem", maxWidth: 480, margin: "0 auto" }}>
      <h2 style={{ marginBottom: "0.5rem" }}>Set a new password</h2>
      <p style={{ opacity: 0.85, marginBottom: "0.75rem" }}>
        The link is <strong>one-time use</strong> (after you set a new password, it stops working).
        You must finish within <strong>15 minutes</strong> of opening this page, and within the
        link expiry time from the email.
      </p>
      {showForm && (
        <p style={{ marginBottom: "1rem", fontWeight: 600, color: "#321B52" }}>
          Time left: {formatCountdown(remainingMs)}
        </p>
      )}

      {expired ? (
        <p style={{ color: "#c0392b" }} role="alert">
          This reset session has expired. Please request a new password reset from the login screen.
        </p>
      ) : null}

      {showForm ? (
      <div className="form-box" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <Inputz
          type="password"
          name="password"
          iconOn="eye_on"
          iconOff="eye_off"
          placeholder="New password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          iconL="lock"
          focusL="lock"
          label="New password"
        />
        <Inputz
          type="password"
          iconOn="eye_on"
          iconOff="eye_off"
          name="confirmPassword"
          placeholder="Confirm password"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          iconL="lock"
          focusL="lock"
          label="Confirm password"
        />
        <Buttonz
          onClick={handleSubmit}
          iconR="login"
          hoverR="login"
          label={loading ? "Saving…" : "Update password"}
        />
      </div>
      ) : null}
      {!payload && token ? (
        <p style={{ color: "#c0392b" }} role="alert">
          Invalid reset link.
        </p>
      ) : null}
      {message ? (
        <p style={{ marginTop: "1rem", color: "#c0392b" }} role="alert">
          {message}
        </p>
      ) : null}
    </div>
  );
}
